/**
 * Coluna ESQUERDA do /chats (320px): novo chat, busca (debounce), filtro de
 * origem, abas Entrada | Meus | Em atendimento | Encerrados e a lista de
 * conversas (polling 8s).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageSquarePlus, Plus, Search } from 'lucide-react';
import type { ChatListItem, ChatOrigin, ChatTab } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import { hasRealtimeEverConnected, useRealtimeConnected } from '@/lib/realtime';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { whatsappChannelsOptions } from '@/features/whatsapp/whatsapp-queries';
import { chatsListOptions } from './chats-queries';
import { useTransientToast } from '@/components/ui/transient-toast';
import {
  ChannelChip,
  ChatAvatar,
  ExternalBotBadge,
  OriginBadge,
  WaitingAttendantBadge,
  chatDisplayName,
  errorMessage,
  formatRelativeTime,
} from './chats-format';
import { NewChatDialog } from './new-chat-dialog';
import { NewWhatsappChatDialog } from './new-whatsapp-chat-dialog';

const TABS: Array<{ value: ChatTab; label: string }> = [
  { value: 'inbox', label: 'Entrada' },
  { value: 'mine', label: 'Meus' },
  { value: 'in_attendance', label: 'Em atendimento' },
  { value: 'archived', label: 'Encerrados' },
];

export function ChatListPane({
  tab,
  onTabChange,
  selectedId,
  onSelect,
}: {
  tab: ChatTab;
  onTabChange: (tab: ChatTab) => void;
  selectedId: number | null;
  onSelect: (chatId: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [origin, setOrigin] = useState<ChatOrigin | ''>('');
  // Filtro por departamento (?desk_id=, v8) — estado do mount, como os
  // filtros vizinhos: nada sobrevive à troca de página nem de usuário.
  const [deskFilter, setDeskFilter] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [newWaOpen, setNewWaOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);

  const desksQ = useQuery(desksListQuery());
  const desks = (desksQ.data ?? []).filter((d) => d.active);

  // Canais WhatsApp conectados: habilitam o botão "Nova conversa" (ativa).
  // retry:false — sem canal (ou sem permissão no endpoint) o botão some.
  const channelsQ = useQuery({ ...whatsappChannelsOptions(), retry: false, staleTime: 5 * 60_000 });
  const waChannels = (channelsQ.data ?? []).filter((c) => c.active);

  // Toast transiente ("Já existe uma conversa aberta…").
  const { toast, showToast } = useTransientToast();

  // SSE conectado ⇒ eventos invalidam a lista; o polling vira fallback lento.
  const realtimeUp = useRealtimeConnected();
  const listQ = useQuery({
    ...chatsListOptions({
      tab,
      search: debouncedSearch,
      origin: origin || undefined,
      deskId: deskFilter === '' ? undefined : Number(deskFilter),
    }),
    refetchInterval: realtimeUp ? 60_000 : 8_000,
  });
  const chats = listQ.data ?? [];
  // Só depois de ter conectado ao menos uma vez: stream caiu ⇒ "reconectando…"
  const reconnecting = !realtimeUp && hasRealtimeEverConnected();

  return (
    <div className="flex w-80 shrink-0 flex-col overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="shrink-0 space-y-2 border-b p-3">
        <div className="flex items-center gap-2">
          <Button size="icon-sm" aria-label="Novo chat" onClick={() => setNewOpen(true)}>
            <Plus strokeWidth={2.5} />
          </Button>
          {waChannels.length > 0 ? (
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Nova conversa no WhatsApp"
              title="Nova conversa (WhatsApp)"
              onClick={() => setNewWaOpen(true)}
            >
              <MessageSquarePlus />
            </Button>
          ) : null}
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar conversa…"
              aria-label="Buscar conversa"
              className="h-8 pl-7 text-sm"
            />
          </div>
          <Select
            value={origin}
            onChange={(e) => setOrigin(e.target.value as ChatOrigin | '')}
            aria-label="Filtrar por origem"
            className="h-8 w-24 shrink-0 px-2 text-xs"
          >
            <option value="">Todos</option>
            <option value="widget">Widget</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="manual">Manual</option>
          </Select>
        </div>

        {/* Filtro por departamento (v8) — só aparece quando há mesa cadastrada. */}
        {desks.length > 0 ? (
          <Select
            value={deskFilter}
            onChange={(e) => setDeskFilter(e.target.value)}
            aria-label="Filtrar por departamento"
            className="h-8 w-full px-2 text-xs"
          >
            <option value="">Todos os departamentos</option>
            {desks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.displayName}
              </option>
            ))}
          </Select>
        ) : null}

        <Tabs value={tab} onValueChange={(v) => onTabChange(v as ChatTab)}>
          <TabsList className="h-8 w-full">
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="px-0.5 text-[11px]">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {reconnecting ? (
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span
              aria-hidden
              className="size-1.5 shrink-0 animate-pulse rounded-full bg-amber-500"
            />
            reconectando…
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {listQ.isPending ? (
          <div aria-busy className="divide-y divide-divider">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5 px-3 py-3">
                <Skeleton className="size-8 shrink-0 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : listQ.isError ? (
          <EmptyState message={`Erro ao carregar conversas: ${errorMessage(listQ.error)}`} />
        ) : chats.length === 0 ? (
          <EmptyState message="Nenhuma conversa" />
        ) : (
          <ul className="divide-y divide-divider">
            {chats.map((chat) => (
              <li key={chat.id}>
                <ChatListRow
                  chat={chat}
                  active={chat.id === selectedId}
                  onSelect={() => onSelect(chat.id)}
                />
              </li>
            ))}
          </ul>
        )}
        {listQ.isFetching && !listQ.isPending && chats.length === 0 ? (
          <div className="flex justify-center p-2">
            <Spinner className="size-4" />
          </div>
        ) : null}
      </div>

      {newOpen ? (
        <NewChatDialog
          onClose={() => setNewOpen(false)}
          onCreated={(chatId) => {
            setNewOpen(false);
            onTabChange('mine');
            onSelect(chatId);
          }}
        />
      ) : null}

      {newWaOpen ? (
        <NewWhatsappChatDialog
          channels={waChannels}
          onClose={() => setNewWaOpen(false)}
          onOpened={(chatId, existing) => {
            setNewWaOpen(false);
            // Conversa criada por mim entra em "Meus"; existente pode estar em
            // qualquer aba — só seleciona (a janela abre pelo id).
            if (!existing) onTabChange('mine');
            onSelect(chatId);
            if (existing) showToast('Já existe uma conversa aberta com este número.');
          }}
        />
      ) : null}

      {toast}
    </div>
  );
}

function ChatListRow({
  chat,
  active,
  onSelect,
}: {
  chat: ChatListItem;
  active: boolean;
  onSelect: () => void;
}) {
  // spec 028: sem nome (a Blip não manda o perfil) aparece o telefone, nunca "?"
  const displayName = chatDisplayName({ requestorName: chat.requestorName });
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active || undefined}
      className={cn(
        'flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors',
        active ? 'bg-accent' : 'hover:bg-accent/50'
      )}
    >
      <ChatAvatar name={displayName} className="mt-0.5" />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold">{displayName}</span>
          <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
            {formatRelativeTime(chat.lastMessageAt ?? chat.createdAt)}
          </span>
        </span>

        {chat.client ? (
          <span className="block truncate text-xs text-muted-foreground">{chat.client.name}</span>
        ) : null}

        <span className="mt-0.5 flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {chat.lastMessagePreview ?? 'Sem mensagens'}
          </span>
          {chat.unreadCount > 0 ? (
            <span
              aria-label={`${chat.unreadCount} mensagens não lidas`}
              className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-destructive px-1 text-[10px] leading-none font-semibold text-white"
            >
              {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
            </span>
          ) : chat.waitingAnswer && chat.status !== 'archived' ? (
            <span
              aria-label="Aguardando resposta"
              title="Aguardando resposta"
              className="size-1.5 shrink-0 rounded-full bg-destructive"
            />
          ) : null}
        </span>

        <span className="mt-1 flex items-center gap-1.5">
          <OriginBadge origin={chat.origin} short className="px-1 py-0 text-[10px]" />
          {/* spec 028 (FR-003): o número da empresa, curto — "PoolTrack · Blip" */}
          {chat.channel ? <ChannelChip channel={chat.channel} short className="max-w-40 text-[10px]" /> : null}
          {/* bot externo ainda conduz (spec 021): na lista, mas fora da fila */}
          {chat.externalBotActive ? <ExternalBotBadge className="px-1" /> : null}
          {/* o chatbot desistiu e avisou o cliente (spec 013): salta aos olhos
              na fila em vez de parecer mais uma conversa parada */}
          {chat.waitingAttendantSince && chat.status === 'inbox' ? (
            <WaitingAttendantBadge className="px-1" />
          ) : null}
          {/* departamento da conversa (v8) — badge discreto quando presente */}
          {chat.desk ? (
            <span className="truncate text-[10px] text-muted-foreground">{chat.desk.name}</span>
          ) : null}
        </span>
      </span>
    </button>
  );
}
